import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import styled from 'styled-components'
import { Col, Row } from 'react-styled-flexboxgrid'
import { useQuery } from '@apollo/client'
import { useRouter } from 'next/router'

import Button from 'components/Atoms/Button'
import Intercom from 'helpers/intercom'
import PageTitle from 'components/Atoms/PageTitle'
import Pagination from 'components/Organisms/Events/Pagination'
import SEO from 'seo/page'
import Text from 'components/Atoms/Text'
import isLightMode from 'helpers/isLightMode'
import { Grid as Template } from 'components/Templates'
import { List, Filters } from 'components/Organisms/Events'
import { NoEventsAvailable } from 'components/Molecules/Events'
import { Trans } from 'i18n-web/i18next'
import { getAccessToken } from 'redux-web/utils/session/actions'
import { getAppName } from 'models/application'
import { getAppRoute } from 'helpers/application'
import { getEventsSortedByDate } from 'models/events'

import lotEvents from '@/graphql/multiparking/queries/lotEvents'

const Title = styled(PageTitle)`
  margin-bottom: 20px;

  .h1 {
    border-bottom: none;
    margin-top: 20px;
    font-size: ${props => props.theme.font.size.mobile.h1};
    color: ${props => props.theme.palette.primary};
    width: 100%;
    line-height: 1;
    margin-bottom: 0;

    @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
      width: 70%;
      font-size: ${props => props.theme.font.size.desktop.h1};
    }

    @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.md}rem) {
      width: 60%;
    }
  }
`

const Panel = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    flex-direction: row;
  }
`

const Subtitle = styled(Text)`
  strong {
    font-weight: 800;
  }
`

const MyPassesButton = styled(Button)`
  margin-bottom: 25px;

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    position: absolute;
    margin-bottom: 0;
    top: 10px;
    right: 8px;
  }
`

const TitleCol = styled(Col)`
  position: relative;
`

const Events = ({
  application,
  bundleList,
  currentPage,
  disableHeaderAndFooter,
  emailSupport,
  eventFilters,
  eventList,
  fetchEvents,
  isSDK,
  loadingEvents,
  loadingPage,
  moreResultsAvailable,
  onBundleSelected,
  onEventSelected,
  pageCount,
  phoneNumberSupport,
  setCurrentPage,
  t,
}) => {
  const router = useRouter()

  const {
    query: { lotId },
  } = router
  const parsedLotId = parseInt(lotId)

  const {
    data: lotEventsData,
    error: lotEventsError,
    loading: lotEventsLoading,
  } = useQuery(lotEvents, { variables: { id: parsedLotId, router } })

  const formattedLotEventsData = getEventsSortedByDate(lotEventsData?.lotEvents)

  const accessToken = getAccessToken()
  const hasLotIdFilter = !!parsedLotId
  const hasBundleList = !isEmpty(bundleList) && !hasLotIdFilter
  const hasEventList =
    !isEmpty(eventList) || (hasLotIdFilter && !isEmpty(formattedLotEventsData))
  const hasPagination =
    (hasEventList || hasBundleList) &&
    !isLoadingEvents &&
    pageCount > 1 &&
    !hasLotIdFilter
  const isLoadingEvents = useMemo(
    () => loadingEvents.status || (hasLotIdFilter && lotEventsLoading),
    [loadingEvents, lotEventsLoading, hasLotIdFilter]
  )
  const events = hasLotIdFilter ? formattedLotEventsData : eventList
  const hasError =
    (!hasEventList && !hasBundleList && !isLoadingEvents) ||
    (hasLotIdFilter && lotEventsError) ||
    (!isLoadingEvents && isEmpty(events))

  const loadMoreEvents = (page, isLastPage) => fetchEvents(page, isLastPage)

  return (
    <Template loading={loadingPage} application={application}>
      <SEO title={`Find Parking for Events | ${getAppName(application)}`} />
      <Row>
        <TitleCol xs={12}>
          <Title noBorder highlight className="h1" title={t('events.title')} />
          {accessToken && isSDK && disableHeaderAndFooter && (
            <MyPassesButton
              className="grayLight absolute"
              disabled={isLoadingEvents}
              onClick={() =>
                router.push(
                  `${getAppRoute(application, 'as')}/my-passes`,
                  `${getAppRoute(application, 'url')}/my-passes`
                )
              }>
              {t('header.menu.buttons.passesAndReceipts')}
            </MyPassesButton>
          )}
          <Panel>
            <Subtitle className="h4 secondary">
              <Trans i18nKey={'events.subtitle'} count={1} />
            </Subtitle>
            {!hasLotIdFilter && (
              <Filters
                setCurrentPage={setCurrentPage}
                setIsLoadingEvents={status => loadingEvents.setStatus(status)}
                filters={eventFilters}
              />
            )}
          </Panel>
        </TitleCol>
        <Col xs={12}>
          {hasBundleList && !isLoadingEvents && (
            <List
              isBundleEvents={true}
              data={bundleList}
              onSelect={onBundleSelected}
              showTitle={hasBundleList && hasEventList}
              title={t('events.bundles.title')}
              application={application}
            />
          )}
          {hasEventList && !isLoadingEvents && (
            <List
              application={application}
              data={events}
              onSelect={onEventSelected}
              showTitle={hasBundleList && hasEventList}
              title={t('events.events.title')}
            />
          )}
          {isLoadingEvents && <List isLoading />}
          {hasError && (
            <NoEventsAvailable
              cta={
                isSDK ? (
                  <Trans
                    i18nKey={
                      emailSupport && phoneNumberSupport
                        ? 'events.noEventsAvailable.callToActionPartners'
                        : !emailSupport
                        ? 'events.noEventsAvailable.callToActionPartnersPhoneNumber'
                        : 'events.noEventsAvailable.callToActionPartnersEmail'
                    }
                    values={{
                      email: emailSupport,
                      phoneNumber: phoneNumberSupport,
                    }}>
                    {emailSupport && (
                      <Text
                        as="a"
                        href={`mailto:${emailSupport}?subject=Questions for ${application} (Web)`}
                        className={classNames('bold link medium', {
                          grayDark: isLightMode(application),
                          contrastText: !isLightMode(application),
                        })}></Text>
                    )}
                    {phoneNumberSupport && (
                      <Text
                        as="a"
                        className={classNames('bold link medium', {
                          grayDark: isLightMode(application),
                          contrastText: !isLightMode(application),
                        })}
                        href={`tel:${phoneNumberSupport}`}></Text>
                    )}
                  </Trans>
                ) : (
                  <Trans i18nKey="events.noEventsAvailable.callToAction">
                    <Text
                      as="span"
                      className={classNames('extraBold', {
                        grayDark: isLightMode(application),
                        contrastText: !isLightMode(application),
                      })}
                      onClick={() => Intercom().sendMessage()}></Text>
                  </Trans>
                )
              }
              description={t('events.noEventsAvailable.description')}
              title={t('events.noEventsAvailable.title')}
            />
          )}
          {hasPagination && (
            <Pagination
              currentPage={currentPage}
              loadMore={loadMoreEvents}
              moreResultsAvailable={moreResultsAvailable}
              pageCount={pageCount}
              setIsLoading={status => loadingEvents.setStatus(status)}
            />
          )}
        </Col>
      </Row>
    </Template>
  )
}

List.propTypes = {
  data: PropTypes.array,
  onSelect: PropTypes.func,
  showTitle: PropTypes.any,
  title: PropTypes.string,
}

Events.propTypes = {
  application: PropTypes.string,
  bundleList: PropTypes.array,
  currentPage: PropTypes.number,
  eventFilters: PropTypes.object,
  eventList: PropTypes.array,
  fetchEvents: PropTypes.func,
  loadingEvents: PropTypes.object,
  loadingPage: PropTypes.bool,
  moreResultsAvailable: PropTypes.bool,
  onBundleSelected: PropTypes.func,
  onEventSelected: PropTypes.func,
  pageCount: PropTypes.number,
  setCurrentPage: PropTypes.func,
  t: PropTypes.func,
  isSDK: PropTypes.bool,
  emailSupport: PropTypes.string,
  phoneNumberSupport: PropTypes.string,
  disableHeaderAndFooter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
}

export default Events
